import { useTranslation } from "react-i18next";

export type OrgLevelLabels = ReturnType<typeof useOrgLevelLabels>;

/**
 * Os nomes dos tipos de objeto organizacional, no idioma da tela.
 *
 * O `level_name` do catálogo é o termo SAP em inglês, e a tela mostra os dois,
 * como o protótipo ("Plant · Centro"). As chaves ficam escritas uma a uma, e não
 * num mapa de dados, porque a trava de i18n só enxerga `t("…")` literal.
 *
 * A frase de "nenhum encontrado" é inteira por tipo, e não montada com o nome:
 * o gênero muda o artigo ("Nenhum centro", "Nenhuma organização de vendas").
 */
export function useOrgLevelLabels() {
  const { t } = useTranslation();

  const labels: Record<string, string> = {
    operating_concern: t("Operating concern"),
    controlling_area: t("Controlling area"),
    credit_control_area: t("Credit control area"),
    company: t("Company"),
    company_code: t("Company code"),
    purchasing_organization: t("Purchasing organization"),
    purchasing_group: t("Purchasing group"),
    plant: t("Plant"),
    storage_location: t("Storage location"),
    shipping_point: t("Shipping point"),
    sales_organization: t("Sales organization"),
    sales_area: t("Sales area"),
    distribution_channel: t("Distribution channel"),
    division: t("Division"),
    sales_office: t("Sales office"),
    sales_group: t("Sales group"),
    warehouse_number: t("Warehouse number"),
  };

  const notFound: Record<string, string> = {
    operating_concern: t("No operating concern found in the legacy system"),
    controlling_area: t("No controlling area found in the legacy system"),
    credit_control_area: t("No credit control area found in the legacy system"),
    company: t("No company found in the legacy system"),
    company_code: t("No company code found in the legacy system"),
    purchasing_organization: t("No purchasing organization found in the legacy system"),
    purchasing_group: t("No purchasing group found in the legacy system"),
    plant: t("No plant found in the legacy system"),
    storage_location: t("No storage location found in the legacy system"),
    shipping_point: t("No shipping point found in the legacy system"),
    sales_organization: t("No sales organization found in the legacy system"),
    distribution_channel: t("No distribution channel found in the legacy system"),
    division: t("No division found in the legacy system"),
    sales_office: t("No sales office found in the legacy system"),
    sales_group: t("No sales group found in the legacy system"),
  };

  return {
    /** Nome traduzido do tipo; tipo fora do catálogo volta com o nome que veio. */
    label: (levelCode: string, fallback: string) => labels[levelCode] ?? fallback,
    /** "Plant · Centro"; quando a tradução é o próprio termo SAP, só ele. */
    typeLine: (levelCode: string, levelName: string) => {
      const local = labels[levelCode];
      return local && local.toLowerCase() !== levelName.toLowerCase()
        ? `${levelName} · ${local}`
        : levelName;
    },
    notFound: (levelCode: string, levelName: string) =>
      notFound[levelCode] ?? t("No {{level}} found in the legacy system", { level: levelName }),
  };
}
